import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import { generateUrls as generateArtworkUrls } from '@hooks/useArtwork'
import { generateUrls as generateCampaignUrls } from '@hooks/useCampaign'
import { generateUrls as generateImageUrls } from '@hooks/useImage'
import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as notificationActions from '@redux/modules/notification'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/notification'
import type { NotificationModel, NotificationRequestOptions } from '@models/types'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

export const generateUrls = (notification: Partial<NotificationModel> = {}) => {
  const { id } = notification

  return {
    listNotificationsIndexUrl: '#/systemManager/notifications?location=list',
    editNotificationUrl: `#/systemManager/notifications/${id}/edit`,
    notificationsIndexUrl: '#/systemManager/notifications?active=true&location=targeted',
    notificationInactiveUrl: '#/systemManager/notifications?active=false&location=targeted',
  }
}

type BuildNotificationsLinkParams = {
  notification: NotificationModel,
  subject?: ReturnType<typeof useRelations>['subject'],
}

const buildNotificationLink = (params: BuildNotificationsLinkParams) => {
  const {
    notification: { link, subject_type },
    subject,
  } = params

  if (!subject?.id || !link) return undefined

  if (subject_type === 'Artwork'){
    const { editArtworkUrl } = generateArtworkUrls(subject, { includeRedirect: true })
    return editArtworkUrl
  }

  if (subject_type === 'Campaign'){
    const { campaignEditDashboardUrl } = generateCampaignUrls(subject)
    return campaignEditDashboardUrl
  }

  if (subject_type === 'CampaignPortalSync'){
    // @ts-expect-error TS2339
    const { campaignPortalsUrl } = generateCampaignUrls({ id: subject.campaign_id })
    return campaignPortalsUrl
  }

  // @ts-expect-error TS2339
  if (subject_type === 'Image' && subject.imageable_type === 'Campaign'){
    const { campaignImageUrl } = generateImageUrls(subject)
    return campaignImageUrl
  }

  return undefined
}

type CreateNotificationParams = {
  dispatch: AppDispatch,
  notificationParams: Partial<NotificationModel>,
  requestOptions?: NotificationRequestOptions,
}

const createNotification = (params: CreateNotificationParams) => {
  const { notificationParams, dispatch, requestOptions } = params
  const { createNotification: createFn } = notificationActions

  const updatedParams = mergeAndStringifyEntityObjects({}, notificationParams, ['options'])

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editNotificationUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editNotificationUrl,
        success,
      }
    }

    return response
  })
}

type UpdateNotificationParams = {
  dispatch: AppDispatch,
  notification: NotificationModel,
  notificationParams: Partial<NotificationModel>,
  requestOptions?: NotificationRequestOptions,
}

const updateNotification = (params: UpdateNotificationParams) => {
  const {
    dispatch, notification, notificationParams, requestOptions,
  } = params
  const { updateNotification: updateFn } = notificationActions

  const updatedParams = mergeAndStringifyEntityObjects(notification, notificationParams, ['options'])

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteNotificationParams = {
  notification: DeleteParams<NotificationModel>,
  dispatch: AppDispatch,
}

const deleteNotification = (params: DeleteNotificationParams) => {
  const { notification, dispatch } = params
  const { deleteNotification: deleteFn } = notificationActions

  return dispatch(deleteFn(notification)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { redirect }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])
      const { notificationsIndexUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: redirect || notificationsIndexUrl,
        success,
      }
    }

    return response
  })
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useNotificationForm(
  notification: Partial<NotificationModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const notificationForm = useForm(
    defaultFormState,
    { entity: notification, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [notification.id, notification.cache_key],
  )

  return {
    ...notificationForm,
  }
}

export function useRelations(notification: NotificationModel) {
  const {
    notifiable_id, notifiable_type, subject_id, subject_type,
  } = notification || {}

  const {
    agencies, artworks, brands, campaigns, campaignPortalSyncs, images, providers,
  } = useSelector(reduxState => reduxState.entities)

  // Notifiable
  const agency = notifiable_type === 'Agency' && notifiable_id ? agencies[notifiable_id] : undefined
  const brand = notifiable_type === 'Brand' && notifiable_id ? brands[notifiable_id] : undefined
  const provider = notifiable_type === 'Provider' && notifiable_id ? providers[notifiable_id] : undefined

  // Subject
  const artwork = subject_type === 'Artwork' && subject_id ? artworks[subject_id] : undefined
  const campaign = subject_type === 'Campaign' && subject_id ? campaigns[subject_id] : undefined
  const campaignPortalSync = subject_type === 'CampaignPortalSync' && subject_id
    ? campaignPortalSyncs[subject_id]
    : undefined
  const image = subject_type === 'Image' && subject_id ? images[subject_id] : undefined

  return {
    agency,
    artwork,
    brand,
    campaign,
    campaignPortalSync,
    image,
    notifiable: agency || brand || provider,
    provider,
    subject: artwork || campaign || campaignPortalSync || image,
  }
}

function useNotification(initEntity: Partial<NotificationModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: notification }: { entity: NotificationModel } = useLatestEntity(initEntity, 'notifications')

  const dispatch = useDispatch()

  const {
    creating, deleting, loaded, loading, updating,
  } = useSelector(reduxState => reduxState.notifications)

  const {
    agency, artwork, brand, campaign, campaignPortalSync, image, notifiable, provider, subject,
  } = useRelations(notification)

  return {
    agency,
    artwork,
    brand,
    callbacks: {
      buildNotificationLink: () => buildNotificationLink({ notification, subject }),
      createNotification: (
        notificationParams: Partial<NotificationModel>,
        requestOptions?: NotificationRequestOptions,
      ) => (
        createNotification({ notificationParams, dispatch, requestOptions })
      ),
      deleteNotification: () => deleteNotification({ notification, dispatch }),
      updateNotification: (
        notificationParams: Partial<NotificationModel>,
        requestOptions?: NotificationRequestOptions,
      ) => (
        updateNotification({
          notification, notificationParams, dispatch, requestOptions,
        })
      ),
    },
    campaign,
    campaignPortalSync,
    creating,
    deleting,
    image,
    loaded,
    loading,
    notifiable,
    notification,
    provider,
    subject,
    updating,
    urls: generateUrls(notification),
  }
}

export type UseNotificationPayload = ReturnType<typeof useNotification>

export default useNotification
